import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { Type } from "@themes/pollinator/ui/Type";
import { DeviceStartFailureHandled } from "@core/features/video";
import { useBrowserCompatibilityFeatures } from "../hooks/useBrowserCompatibilityFeatures";
import { useDevices } from "../providers/DevicesProvider";
import VideoMeetingPermissionsTip from "@platform/features/videoMeeting/components/VideoMeetingPermissions/VideoMeetingPermissionsTip";


// Helper function to detect iOS devices
const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
};

type Props = {
  isActionLoading: boolean;
  isDevicesLoading: boolean;
  onJoinMeeting: () => Promise<void>;
  roomId?: number | null;
};

const MeetingJoinDetails = ({
  isActionLoading,
  isDevicesLoading,
  onJoinMeeting,
  roomId,
}: Props) => {
  const { isUserSupported, isSupported: isBrowserSupported, hasAudioDevices, hasVideoDevices } = useBrowserCompatibilityFeatures();
  const { selectedAudioOutputDevice, audioOutputDevices, permissionDenied, audioPermissionGuidance } = useDevices();

  const [isLoading, setIsLoading] = useState(false);
  const [showNoSpeakersMessage, setShowNoSpeakersMessage] = useState(false);

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Check if devices are ready (not loading - allow joining even without devices)
  const areDevicesReady = !isDevicesLoading;

  // Check if speaker source is selected (for display purposes only - not used to disable join)
  const isIOS = isIOSDevice();
  const hasSpeakerSelected = isIOS || selectedAudioOutputDevice !== null;

  const [showIOSSpeakerNote, setShowIOSSpeakerNote] = useState(false);

  // Handle delayed display of no speakers message
  useEffect(() => {
    // On iOS, show a softer message instead of suppressing entirely
    if (isIOS) {
      if (!isDevicesLoading && audioOutputDevices.length === 0) {
        setShowIOSSpeakerNote(true);
      } else {
        setShowIOSSpeakerNote(false);
      }
      setShowNoSpeakersMessage(false);
      return;
    }

    // Reset the message when devices are loading
    if (isDevicesLoading) {
      setShowNoSpeakersMessage(false);
      return;
    }

    // If devices are not loading and we have no speakers, show message after a delay
    if (!isDevicesLoading && audioOutputDevices.length === 0 && !hasSpeakerSelected) {
      const timer = setTimeout(() => {
        setShowNoSpeakersMessage(true);

        // Log for debugging
        if (roomId) {
          console.warn('[MeetingJoinDetails] No speaker devices detected', {
            roomId,
            audio_output_devices_count: audioOutputDevices.length,
          });
        }
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    } else {
      // If we have speakers or a speaker is selected, hide the message
      setShowNoSpeakersMessage(false);
    }
  }, [isDevicesLoading, audioOutputDevices.length, hasSpeakerSelected, isIOS, roomId]);

  const handleJoinMeeting = async () => {
    setIsLoading(true);
    setIsErrorDialogOpen(false);

    try {
      await onJoinMeeting();
    } catch (error) {
      // Sentinel: device-start failure already wrote sessionError +
      // sessionErrorDetail upstream; VideoJoinPage's sessionError branch
      // renders VideoMeetingAccessError. Returning silently avoids layering
      // a dialog on top of the access-error surface (or, after unmount,
      // attempting to set state on an unmounting component).
      if (error instanceof DeviceStartFailureHandled) {
        return;
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      console.error("[MeetingJoinDetails] Join error:", error);
      setJoinError(message);
      setIsErrorDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button text based on state
  const getButtonText = () => {
    if (isLoading) return "Loading...";
    if (isDevicesLoading) return "Loading Devices...";
    if (!areDevicesReady) return "Loading...";
    if (isActionLoading) return "Loading...";

    return "Join Meeting";
  };

  // Explain why the join button is disabled
  const getDisabledReason = (): string | null => {
    if (isLoading) return "Joining meeting...";
    if (isDevicesLoading) return "Loading devices...";
    if (isActionLoading) return "Loading...";
    if (!isUserSupported && permissionDenied) return null; // handled by permission message below
    if (!isUserSupported) return null; // handled by browser message below
    return null;
  };

  const disabledReason = getDisabledReason();

  return (
    <>
      <div className="flex flex-col gap-2">
        <Button
          onClick={handleJoinMeeting}
          disabled={
            isLoading ||
            isDevicesLoading ||
            !areDevicesReady ||
            isActionLoading ||
            !isUserSupported
          }
          aria-describedby={disabledReason ? "join-status" : undefined}
        >
          {getButtonText()}
        </Button>
        {disabledReason && (
          <span id="join-status" className="sr-only">
            {disabledReason}
          </span>
        )}
        {!isDevicesLoading && !isActionLoading && !isUserSupported && (
          <div className="flex items-start gap-1">
            <AlertCircle className="h-4 w-4 text-error-700 mt-0.5" />
            {!isBrowserSupported ? (
              // Genuine browser incompatibility — Chime SDK rejects this browser.
              <Type variant="caption" className="text-error-700">
                This browser is not supported. Please try a different one.
              </Type>
            ) : permissionDenied ? (
              // Initial getUserMedia threw NotAllowedError.
              <Type variant="caption" className="text-error-700">
                Camera and microphone access is blocked.{" "}
                <VideoMeetingPermissionsTip />
              </Type>
            ) : !hasAudioDevices && !hasVideoDevices ? (
              // Browser is supported, permission isn't explicitly denied, but
              // no devices enumerated. Most common cause: OS-level Privacy
              // permission for the browser is off, so the browser can't see
              // the camera/mic even though the per-site permission says
              // "Allowed". The browser had to be restarted after the OS
              // toggle for the new permission to take effect.
              <Type variant="caption" className="text-error-700">
                We couldn't detect a camera or microphone. Make sure they're
                connected and that your OS allows your browser to use them.{" "}
                <VideoMeetingPermissionsTip />
              </Type>
            ) : !hasAudioDevices ? (
              <Type variant="caption" className="text-error-700">
                We couldn't detect a microphone. Check your connections and
                your OS settings for browser microphone access.{" "}
                <VideoMeetingPermissionsTip />
              </Type>
            ) : (
              // Fallthrough: hasAudioDevices but somehow still not supported
              // (shouldn't happen given the hook's logic, but keeps the
              // surface honest if the gate-shape changes later).
              <Type variant="caption" className="text-error-700">
                We couldn't fully initialize your devices. Try refreshing the
                page.{" "}
                <VideoMeetingPermissionsTip />
              </Type>
            )}
          </div>
        )}
        {!isDevicesLoading && !isActionLoading && isUserSupported && showNoSpeakersMessage && (
          <div className="flex flex-col gap-2 p-3 border border-error-300 rounded-md bg-error-50">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-error-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <Type variant="caption" className="text-error-700 font-medium block mb-1">
                  No speaker source selected
                </Type>
                <Type variant="caption" className="text-error-600 block">
                Your browser is most likely using your default speaker. To confirm this click the "Test speakers" button, if you hear the tones, then your audio should be okay in the meeting. If not, please refresh or try a different browser.
                </Type>
              </div>
            </div>
          </div>
        )}
        {showIOSSpeakerNote && (
          <div className="flex items-start gap-2 p-3 border border-brand-300 rounded-md bg-brand-50">
            <AlertCircle className="h-4 w-4 text-brand-700 mt-0.5 flex-shrink-0" />
            <Type variant="caption" className="text-brand-700">
              Your device's built-in speaker will be used.
            </Type>
          </div>
        )}
        {audioPermissionGuidance === 'mic-blocked' && (
          <div className="flex items-start gap-2 p-3 border border-error-300 rounded-md bg-error-50">
            <AlertCircle className="h-4 w-4 text-error-700 mt-0.5 flex-shrink-0" />
            <Type variant="caption" className="text-error-700">
              Microphone access is blocked. Go to your device&apos;s{" "}
              <strong>Settings → Apps</strong>, find your browser, and turn on{" "}
              <strong>Microphone</strong>, then reload this page.
            </Type>
          </div>
        )}
      </div>
      {isErrorDialogOpen && (
        <Dialog
          isOpen
          onClose={(): void => setIsErrorDialogOpen(false)}
          title="Unable to Join Meeting"
          description={`There was an issue joining this meeting: ${joinError || "Unknown error"}. Check your connectivity and try again.`}
        />
      )}
    </>
  );
};

export default MeetingJoinDetails;
